'use client';
// Bloc d'inscription à la newsletter, affiché en bas de chaque guide.
//
// Constat du 08/08/2026 : le formulaire d'inscription n'existait QUE sur la page
// vitrine. Les 70 articles — la seule chose qui ramène du monde depuis Google —
// n'en proposaient aucun. Résultat : un seul abonné en base. Un visiteur arrivait
// par une recherche, lisait, repartait, et on ne le revoyait jamais.
//
// RGPD Art. 6.1.a : case NON pré-cochée, bouton inactif tant qu'elle ne l'est
// pas. C'est le même contrat que le formulaire de la vitrine — le serveur
// (/api/newsletter) refuse de toute façon en 400 sans consentement.
import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { useRecaptcha } from '@/app/lib/recaptcha';
// ⚠️ LA DÉDUCTION DE LANGUE A ÉTÉ EXTRAITE le 02/09/2026 (`langue-guide.js`).
// Elle était née ici, et c'est pour ça que ce bloc était le SEUL du pied de
// page à s'afficher en néerlandais pendant que tout le reste restait français.
// Elle sert maintenant à quatre endroits : la recopier une fois de plus, ce
// serait quatre occasions de diverger sur ce que veut dire « ce guide est en
// néerlandais ».
import { slugDuGuide, langueDuGuide } from '@/app/lib/langue-guide';

const T = {
  fr: {
    titre: 'Recevoir les nouveaux guides',
    sous: 'Un email quand un guide paraît : barèmes, indexations, échéances ONSS. Rien d’autre.',
    ph: 'votre@email.be',
    btn: 'S’inscrire',
    note: 'J’accepte de recevoir la newsletter. Désinscription en un clic, à tout moment.',
    ok: 'Inscription confirmée. Vérifiez votre boîte mail.',
    err: 'Inscription impossible pour le moment. Réessayez plus tard.',
  },
  nl: {
    titre: 'Ontvang de nieuwe gidsen',
    sous: 'Eén e-mail wanneer er een gids verschijnt: barema’s, indexeringen, RSZ-termijnen. Niets anders.',
    ph: 'uw@email.be',
    btn: 'Inschrijven',
    note: 'Ik ga akkoord met het ontvangen van de nieuwsbrief. Uitschrijven met één klik, op elk moment.',
    ok: 'Inschrijving bevestigd. Controleer uw mailbox.',
    err: 'Inschrijving momenteel niet mogelijk. Probeer het later opnieuw.',
  },
};

/**
 * @param {{toujours?: boolean}} props  `toujours` force l'affichage hors article
 *   (l'index /blog l'utilise). Sans lui, le composant se retire de lui-même
 *   partout sauf sur un guide — c'est ce qui permet de le poser une seule fois
 *   dans le layout sans le voir apparaître en double sur l'index.
 */
export default function NewsletterInline({ toujours = false }) {
  const pathname = usePathname();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [etat, setEtat] = useState('idle'); // idle | envoi | ok | erreur
  const { getToken } = useRecaptcha();

  // R8 — la langue vient du registre, pas d'une prop à passer dans 82 fichiers :
  // un guide néerlandais propose son formulaire en néerlandais.
  const slug = slugDuGuide(pathname);
  const lang = langueDuGuide(pathname);
  const t = T[lang];

  if (!slug && !toujours) return null;

  const envoyer = async () => {
    if (!email || !consent || etat === 'envoi') return;
    setEtat('envoi');
    try {
      const recaptchaToken = await getToken('newsletter');
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, lang, source: 'blog', consent: true, recaptchaToken }),
      });
      setEtat(res.ok ? 'ok' : 'erreur');
    } catch {
      setEtat('erreur');
    }
  };

  return (
    <section style={{ marginTop: 40, padding: 24, borderRadius: 12, background: '#111827', border: '1px solid #1e293b' }}>
      <h3 style={{ color: '#f0c040', fontSize: 18, margin: '0 0 6px' }}>{t.titre}</h3>
      <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>{t.sous}</p>

      {etat === 'ok' ? (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.3)', color: '#86efac', fontSize: 14 }}>
          {t.ok}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="email"
              value={email}
              placeholder={t.ph}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && envoyer()}
              style={{ flex: '1 1 240px', minWidth: 0, padding: '11px 14px', borderRadius: 8, border: '1px solid #334155', background: '#0a0a0f', color: '#e2e8f0', fontSize: 14 }}
            />
            <button
              onClick={envoyer}
              disabled={!email || !consent || etat === 'envoi'}
              style={{
                padding: '11px 22px', borderRadius: 8, border: 'none', cursor: (!email || !consent) ? 'not-allowed' : 'pointer',
                background: '#f0c040', color: '#0a0a0f', fontWeight: 700, fontSize: 14,
                opacity: (!email || !consent || etat === 'envoi') ? 0.5 : 1,
              }}
            >
              {etat === 'envoi' ? '…' : t.btn}
            </button>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={{ marginTop: 2, accentColor: '#f0c040', flexShrink: 0 }}
            />
            <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
              {t.note}{' '}
              <a href={`/legal/privacy?lang=${lang}`} style={{ color: '#94a3b8' }}>Politique de confidentialité</a>
            </span>
          </label>

          {etat === 'erreur' && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#fca5a5' }}>{t.err}</div>
          )}
        </>
      )}
    </section>
  );
}
